import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, LayoutDashboard } from "lucide-react";

export function DashboardPreferences() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const { data: preferences, isLoading } = useQuery({
    queryKey: ["dashboard-preferences"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("dashboard_preferences" as any)
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      
      // Return defaults if no preferences exist
      return data || {
        user_id: user.id,
        widget_visibility: {
          totalDeals: true,
          pendingReview: true,
          activeAthletes: true,
          complianceRate: true,
          dealsByStatus: true,
          dealsByCategory: true,
          recentActivity: true,
          upcomingDeadlines: true,
        },
        default_date_range: "30",
      };
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (updates: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("dashboard_preferences" as any)
        .upsert({
          user_id: user.id,
          ...updates,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard-preferences"] });
      toast({
        title: "Success",
        description: "Dashboard preferences saved successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = async () => {
    setIsSaving(true);
    await saveMutation.mutateAsync(preferences);
    setIsSaving(false);
  };

  const toggleWidget = (widgetKey: string) => {
    if (!preferences) return;
    const vis = (preferences as any).widget_visibility;
    if (!vis) return;
    
    const newVisibility = {
      ...vis,
      [widgetKey]: !vis[widgetKey],
    };

    saveMutation.mutate({
      widget_visibility: newVisibility,
      default_date_range: (preferences as any).default_date_range,
    });
  };

  const handleDateRangeChange = (value: string) => {
    if (!preferences) return;
    saveMutation.mutate({
      widget_visibility: (preferences as any).widget_visibility,
      default_date_range: value,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="border-2 hover:border-primary/20 transition-colors">
      <CardHeader>
        <div className="flex items-center gap-2">
          <LayoutDashboard className="h-5 w-5 text-primary" />
          <CardTitle>Dashboard Preferences</CardTitle>
        </div>
        <CardDescription>
          Customize which widgets appear on your dashboard and default settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="font-semibold">Default Date Range</h3>
          <Select 
            value={(preferences as any)?.default_date_range || "30"} 
            onValueChange={handleDateRangeChange}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold">Widget Visibility</h3>
          <p className="text-sm text-muted-foreground">Choose which widgets to display on your dashboard</p>
          
          <div className="space-y-3">
            {Object.entries((preferences as any)?.widget_visibility || {}).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <Label htmlFor={key} className="cursor-pointer">
                  {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                </Label>
                <Switch
                  id={key}
                  checked={value as boolean}
                  onCheckedChange={() => toggleWidget(key)}
                />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
